// @avantia common-code
import { getFromWindowRef, intervalToMs, SMap } from '@avantia/client-and-server-utilities';
import cookies from 'js-cookie';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { HpClientCookie, HpCookies } from '../types';
import { isInternetExplorer } from './ie-hacks';

export interface HpCookieStore {
  getItem: (name: string) => string | null;
  setItem: (cookie: HpClientCookie) => void;
}

export type HpCreateCookieStoreFunction = () => HpCookieStore;

export const createFakeStore: HpCreateCookieStoreFunction = fakeStoreCreator();

let storage: HpCookieStore;

registerConfigurationAction((config) => {
  const { cookieScope } = config;
  storage = createStorage(cookieScope);
});

const isIE = isInternetExplorer();

export function getCookie(name: string, cookies?: HpCookies): string | null {
  const value = cookies ? cookies.get(name) : storage.getItem(name);
  return typeof value === 'string' ? value : null;
}

export function setCookie(cookie: HpClientCookie, cookies?: HpCookies): void {
  cookies ? cookies.set(cookie) : storage.setItem(cookie);
}

export function createCookieProxy(): HpCookies {
  return {
    get: getCookie,
    set: setCookie
  } as HpCookies;
}

function createStorage(cookieScope: string): HpCookieStore {
  const nameToKey = defaultNameToKey;
  const { sessionStorage, document } = getFromWindowRef<HpWindowDetail>(['sessionStorage', 'document']) || {};
  if (cookieScope === 'per-tab' && sessionStorage) {
    return {
      getItem: (name) => sessionStorage.getItem(nameToKey(name)),
      setItem: ({ name, value }) => sessionStorage.setItem(nameToKey(name), value)
    };
  } else if (cookieScope === 'persistent' && document) {
    return createCookieStore();
  } else if (cookieScope !== 'per-tab' && cookieScope !== 'persistent') {
    throw new Error(`The cookieScope is invalid: "${cookieScope}".`);
  }

  return createFakeStore();
}

export interface HpCreateCookieStoreOptions {
  disableNameToKeyMapping?: true | undefined;
}

/**
 * Creates a cookie store with real cookies. Do not use if you need a store with session storage.
 */
export function createCookieStore(options?: HpCreateCookieStoreOptions): HpCookieStore {
  const { disableNameToKeyMapping } = options || {};
  const nameToKey = disableNameToKeyMapping ? (name: string) => name : defaultNameToKey;
  return {
    setItem: (cookie) => {
      const { domain, expires, name, path, value } = cookie;
      cookies.set(nameToKey(name), value, {
        path,
        domain,
        expires: expires === undefined ? new Date().getTime() + intervalToMs('30d', { unbounded: true }) : expires
      });
    },
    getItem: (name) => {
      const value = cookies.get(nameToKey(name));
      return typeof value === 'string' ? value : null;
    }
  };
}

function defaultNameToKey(name: string): string {
  if (!isIE) {
    return name;
  }

  const { name: suffix } = getFromWindowRef<HpWindowDetail>(['name']) || {};
  const key = name + (suffix || '');
  return key;
}

/**
 * Only used in non-browser (e.g. test) environments.
 * We only ever want to have one instance of the fake store.
 * This is needed to test cookie functionality in unit tests.
 * @returns a fake cookie store.
 */
function fakeStoreCreator(): HpCreateCookieStoreFunction {
  const data: SMap<string> = {};
  const fake: HpCookieStore = {
    setItem: (cookie) => {
      const { name, value } = cookie;
      data[name] = value;
    },
    getItem: (name) => data[name]
  };

  return () => fake;
}

interface HpWindowDetail {
  document: any;
  name: string;
  sessionStorage: Storage;
}
